import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "../../components/Select";
import styles from "./styles.module.scss";

interface WiFiChannelGraphProps {
  data: {
    ssid: string;
    channelStart: number;
    channelEnd: number;
    decibel: number;
    color: string;
  }[];
}

// Constants for X and Y axis data
const maxDbm = -20;
const minDbm = -100;
const yStep = 10;

/**
 * This component takes in data for showing the graph in canvas
 */
const WiFiChannelGraph = ({ data }: WiFiChannelGraphProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Channel filter for showing specified channels on Y axis
  const [channelFilter, setChannelFilter] = useState({
    value: "1-14",
    start: 1,
    end: 14,
  });

  // Filter networks from main data based on the available network in channel
  const networks = useMemo(() => {
    return data?.filter(
      (v) =>
        (v.channelStart >= channelFilter.start && v.channelStart <= channelFilter.end) ||
        (v.channelEnd >= channelFilter.start && v.channelEnd <= channelFilter.end)
    );
  }, [channelFilter, data]);

  // Find x position to start printing on canvas
  const channelToX = (channel: number, minChannel: number, maxChannel: number, padding: number, canvasWidth: number) => {
    return padding + ((channel - minChannel) * (canvasWidth - padding * 3)) / (maxChannel - minChannel);
  };

  // Find y position to start printing on canvas
  const dbmToY = (padding: number, dbm: number, canvasHeight: number) => {
    return padding + ((maxDbm - dbm) * (canvasHeight - padding * 3)) / (maxDbm - minDbm);
  };

  // Convert HEX value to RGB to print on canvas
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // This function is responsible for plotting all pixels on canvas for Y axis
  const drawYAxis = useCallback(
    (ctx: CanvasRenderingContext2D, axisFontSize: number, padding: number, canvasHeight: number, canvasWidth: number) => {
      // Set font styling and print label on Y axis
      ctx.font = `bold ${axisFontSize}px Arial`;
      ctx.textAlign = "center";
      ctx.fillStyle = "white";
      ctx.translate(padding * 0.5, canvasHeight / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText("Signal Strength (dBm)", 0, 0);
      ctx.restore();

      // Print decibel value on Y axis
      for (let dbmValue = -20; dbmValue >= -100; dbmValue -= yStep) {
        const yPos = dbmToY(padding, dbmValue, canvasHeight);

        ctx.strokeStyle = "rgba(128, 128, 128, 0.6)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(padding + 50, yPos);
        ctx.lineTo(canvasWidth - padding, yPos);
        ctx.stroke();

        ctx.font = `bold ${axisFontSize}px Arial`;
        ctx.textAlign = "center";
        ctx.fillStyle = "white";
        ctx.fillText(dbmValue.toString(), padding * 1.2, yPos);
        ctx.restore();
      }
    },
    []
  );

  // This function is responsible for plotting all pixels on canvas for X axis
  const drawXAxis = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      axisFontSize: number,
      minChannel: number,
      maxChannel: number,
      padding: number,
      canvasHeight: number,
      canvasWidth: number
    ) => {
      ctx.font = `bold ${axisFontSize}px Arial`;
      ctx.fillText("Wi-Fi Channels", canvasWidth / 2, canvasHeight - padding * 0.5);
      ctx.restore();

      ctx.textAlign = "center";
      ctx.textBaseline = "top";

      const numChannels = maxChannel - minChannel + 1;
      const xLabelStep = numChannels <= 20 ? 1 : numChannels <= 40 ? 2 : 3;

      for (let ch = minChannel; ch <= maxChannel; ch += xLabelStep) {
        const xPos = channelToX(ch, minChannel, maxChannel, padding, canvasWidth) + 50;

        ctx.strokeStyle = "rgba(128, 128, 128, 0.6)";
        ctx.beginPath();
        ctx.moveTo(xPos, padding);
        ctx.lineTo(xPos, canvasHeight - padding * 2);
        ctx.stroke();

        ctx.fillText(ch.toString(), xPos, canvasHeight - padding * 1.5);
      }
    },
    []
  );

  // draw network graph on canvas
  const drawNetworkGraph = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      gPadding: number,
      minChannel: number,
      maxChannel: number,
      canvasHeight: number,
      canvasWidth: number,
      baseFontSize: number
    ) => {
      const skewOffset = canvasWidth * 0.01;
      const baseCurveRadius = canvasWidth * 0.005;
      const minShapeWidth = canvasWidth * 0.05;
      const labelFontSize = baseFontSize * 0.7;

      // Loop over each network item and plot graph for it
      networks.forEach((network) => {
        const rawStartX = channelToX(network.channelStart, minChannel, maxChannel, gPadding, canvasWidth) + 50;
        const rawEndX = channelToX(network.channelEnd, minChannel, maxChannel, gPadding, canvasWidth) + 50;

        const rawWidth = rawEndX - rawStartX;
        const shapeWidth = Math.max(rawWidth, minShapeWidth);
        const shapeCenter = (rawStartX + rawEndX) / 2;

        const startX = shapeCenter - shapeWidth / 2;
        const endX = shapeCenter + shapeWidth / 2;

        const topY = dbmToY(gPadding, network.decibel, canvasHeight);
        const bottomY = canvasHeight - gPadding * 2;

        const actualCurveRadius = Math.min(baseCurveRadius, shapeWidth / 4);

        ctx.fillStyle = hexToRgba(network.color, 0.3);
        ctx.beginPath();
        ctx.moveTo(startX, bottomY);

        ctx.lineTo(startX + skewOffset, topY + actualCurveRadius);
        ctx.quadraticCurveTo(startX + skewOffset, topY, startX + skewOffset + actualCurveRadius, topY);

        ctx.lineTo(endX - skewOffset - actualCurveRadius, topY);
        ctx.quadraticCurveTo(endX - skewOffset, topY, endX - skewOffset, topY + actualCurveRadius);

        ctx.lineTo(endX, bottomY);

        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = network.color;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(startX, bottomY);

        ctx.lineTo(startX + skewOffset, topY + actualCurveRadius);
        ctx.quadraticCurveTo(startX + skewOffset, topY, startX + skewOffset + actualCurveRadius, topY);

        ctx.lineTo(endX - skewOffset - actualCurveRadius, topY);
        ctx.quadraticCurveTo(endX - skewOffset, topY, endX - skewOffset, topY + actualCurveRadius);
        ctx.lineTo(endX, bottomY);

        ctx.stroke();

        const ssid = network.ssid;
        const originalLabelX = (startX + endX) / 2;
        const originalLabelY = topY - 10;

        ctx.font = `${labelFontSize}px Arial`;
        const textWidth = ctx.measureText(ssid).width;
        const textHeight = labelFontSize;

        ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
        const padding = labelFontSize * 0.5;
        ctx.fillRect(
          originalLabelX - textWidth / 2 - padding,
          originalLabelY - textHeight - padding,
          textWidth + padding * 2,
          textHeight + padding * 2
        );

        ctx.fillStyle = network.color;
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(ssid, originalLabelX, originalLabelY);
      });
    },
    [networks]
  );

  const drawWifiChannelGraph = useCallback(() => {
    const canvas = canvasRef.current;
    const container = canvasRef?.current?.parentElement;

    if (canvasRef.current && canvas && container) {
      canvasRef.current.width = container.clientWidth;
      canvasRef.current.height = container.clientHeight;

      const ctx = canvas.getContext("2d");

      // Check that canvas is loaded and context is ready to print
      if (ctx) {
        const canvasHeight = canvas.height;
        const canvasWidth = canvas.width;

        // Clear old pixels from canvas before printing
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        const padding = canvasWidth * 0.04;

        let minChannel: number, maxChannel: number;
        if (networks.length) {
          minChannel = networks.reduce((min, network) => Math.min(min, network.channelStart), networks?.[0]?.channelStart) - 2;
          maxChannel = networks.reduce((max, network) => Math.max(max, network.channelEnd), networks?.[0]?.channelEnd) + 2;
        } else {
          minChannel = channelFilter.start - 2;
          maxChannel = channelFilter.end - 2;
        }

        const baseFontSize = Math.max(canvasWidth * 0.015, 10);
        const axisFontSize = baseFontSize * 0.8;

        ctx.font = `bold ${baseFontSize}px Arial`;
        ctx.fillStyle = "white";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.save();

        drawYAxis(ctx, axisFontSize, padding, canvasHeight, canvasWidth);
        drawXAxis(ctx, axisFontSize, minChannel, maxChannel, padding, canvasHeight, canvasWidth);
        drawNetworkGraph(ctx, padding, minChannel, maxChannel, canvasHeight, canvasWidth, baseFontSize);
      }
    }
  }, [networks, drawYAxis, drawXAxis, drawNetworkGraph]);

  const handleResize = useCallback(() => {
    const container = canvasRef?.current?.parentElement;

    if (canvasRef.current && container) {
      canvasRef.current.width = container.clientWidth;
      canvasRef.current.height = container.clientHeight;

      drawWifiChannelGraph();
    }
  }, [drawWifiChannelGraph]);

  useEffect(() => {
    drawWifiChannelGraph();
  }, [drawWifiChannelGraph]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  return (
    <div className={styles.container}>
      <div className={styles.filterContainer}>
        <p>WiFi Channels</p>
        <Select
          selectProps={{
            value: channelFilter.value,
            onChange: (event) => {
              const [start, end] = event.target.value.split("-");
              setChannelFilter({
                value: event.target.value,
                start: +start,
                end: +end,
              });
            },
          }}
          options={[
            { label: "1-14 (2.4GHz)", value: "1-14" },
            { label: "36-64", value: "36-64" },
            { label: "100-144", value: "100-144" },
            { label: "149-177", value: "149-177" },
          ]}
        />
      </div>
      <div className={styles.canvasContainer}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};

export default WiFiChannelGraph;
